import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Snackbar,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import ReceiptOutlinedIcon from '@material-ui/icons/ReceiptOutlined';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import { correctBillFields, getProveedoresLector, linkBill, unlinkBill } from '../../services/billing-v2';
import { getNodes } from '../../services/hierarchy';

const useStyles = makeStyles(theme => ({
    root: {
        padding: theme.spacing(2),
        background: `linear-gradient(180deg, ${theme.palette.surface.panel} 0%, ${theme.palette.background.default} 100%)`,
    },
    pageTitle: {
        marginBottom: theme.spacing(2),
        fontWeight: 700,
        color: theme.palette.text.primary,
    },
    uploadBar: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1.5),
        border: `1px solid ${theme.palette.border.soft}`,
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        backgroundColor: theme.palette.surface.soft,
        boxShadow: theme.palette.shadow.card,
    },
    uploadBarInner: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    uploadActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
    },
    uploadHint: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.palette.text.secondary,
        fontSize: 13,
    },
    primaryButton: {
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        padding: theme.spacing(1, 2),
        textTransform: 'none',
        fontWeight: 700,
        color: theme.palette.common.white,
        background: `linear-gradient(180deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
        boxShadow: theme.palette.shadow.accent,
        '&:hover': {
            background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        },
    },
    secondaryButton: {
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        padding: theme.spacing(1, 2),
        textTransform: 'none',
        fontWeight: 700,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.background.default,
        border: `1px solid ${theme.palette.border.button}`,
    },
    dangerButton: {
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        padding: theme.spacing(1, 2),
        textTransform: 'none',
        fontWeight: 700,
    },
    column: {
        display: 'flex',
    },
    panel: {
        width: '100%',
        border: `1px solid ${theme.palette.border.soft}`,
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        overflow: 'hidden',
        boxShadow: theme.palette.shadow.card,
        backgroundColor: theme.palette.background.default,
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(1.2, 1.75),
        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
        color: theme.palette.common.white,
    },
    sectionHeaderNeutral: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(1.2, 1.75),
        backgroundColor: theme.palette.surface.soft,
        borderBottom: `1px solid ${theme.palette.border.soft}`,
        color: theme.palette.text.primary,
    },
    sectionHeaderLeft: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        fontWeight: 700,
        fontSize: 13,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
    },
    sectionHeaderRight: {
        fontSize: 12,
        fontWeight: 700,
    },
    panelBody: {
        padding: theme.spacing(1.5),
    },
    fieldGroup: {
        marginBottom: theme.spacing(0.5),
    },
    compactFieldsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: theme.spacing(1.25),
        marginBottom: theme.spacing(1.5),
        '@media (max-width: 900px)': {
            gridTemplateColumns: '1fr',
        },
    },
    fieldLabel: {
        marginBottom: theme.spacing(0.5),
        fontSize: 12,
        fontWeight: 600,
        color: theme.palette.text.secondary,
    },
    textField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 4,
            backgroundColor: theme.palette.surface.popover,
            boxShadow: 'inset 0 0 0 var(--confidence-border-width, 0px) var(--confidence-color, transparent)',
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--confidence-color, rgba(0, 0, 0, 0.23))',
                borderWidth: 'var(--confidence-border-width, 1px)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--confidence-color, rgba(0, 0, 0, 0.87))',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--confidence-color, currentColor)',
                borderWidth: 'var(--confidence-border-width, 2px)',
            },
            '&.Mui-focused': {
                boxShadow: 'inset 0 0 0 var(--confidence-border-width, 0px) var(--confidence-color, transparent)',
            },
        },
        '& .MuiOutlinedInput-input': {
            padding: theme.spacing(1, 1.25),
            fontSize: 13,
        },
        '& .MuiSelect-outlined.MuiSelect-outlined': {
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
        },
    },
    serviceTypeGroup: {
        marginTop: theme.spacing(0.5),
        '& .MuiTypography-root': {
            fontSize: 13,
        },
    },
    fileActions: {
        display: 'flex',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    itemsTable: {
        display: 'grid',
        gap: 1,
        backgroundColor: theme.palette.border.soft,
        border: `1px solid ${theme.palette.border.soft}`,
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        overflow: 'hidden',
    },
    tableRow: {
        display: 'grid',
        gridTemplateColumns: 'minmax(220px, 2.4fr) repeat(4, minmax(120px, 1fr))',
        gap: 1,
        backgroundColor: theme.palette.border.soft,
        '@media (max-width: 1280px)': {
            gridTemplateColumns: 'minmax(180px, 2fr) repeat(4, minmax(100px, 1fr))',
        },
        '@media (max-width: 1100px)': {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
    },
    tableHeaderCell: {
        padding: theme.spacing(1, 1.25),
        backgroundColor: theme.palette.surface.soft,
        fontSize: 12,
        fontWeight: 700,
        color: theme.palette.text.secondary,
    },
    tableCell: {
        padding: theme.spacing(0.75, 1),
        backgroundColor: theme.palette.background.default,
    },
    tableTextField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 4,
            backgroundColor: theme.palette.background.default,
            boxShadow: 'inset 0 0 0 var(--confidence-border-width, 0px) var(--confidence-color, transparent)',
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--confidence-color, rgba(0, 0, 0, 0.23))',
                borderWidth: 'var(--confidence-border-width, 1px)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--confidence-color, rgba(0, 0, 0, 0.87))',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--confidence-color, currentColor)',
                borderWidth: 'var(--confidence-border-width, 2px)',
            },
            '&.Mui-focused': {
                boxShadow: 'inset 0 0 0 var(--confidence-border-width, 0px) var(--confidence-color, transparent)',
            },
        },
        '& .MuiOutlinedInput-input': {
            padding: theme.spacing(0.75, 1),
            fontSize: 13,
            textAlign: 'right',
        },
    },
    conceptField: {
        '& .MuiOutlinedInput-input': {
            textAlign: 'left',
        },
    },
    addLink: {
        marginTop: theme.spacing(1),
        padding: 0,
        textTransform: 'none',
        fontWeight: 700,
        minWidth: 'auto',
    },
    sideSection: {
        border: `1px solid ${theme.palette.border.soft}`,
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.default,
    },
    infoGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
        '@media (max-width: 1100px)': {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
        '@media (max-width: 640px)': {
            gridTemplateColumns: '1fr',
        },
    },
    infoCard: {
        padding: theme.spacing(1.25, 1.5),
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.soft}`,
        backgroundColor: theme.palette.surface.popover,
    },
    infoCardFields: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: theme.spacing(1),
        '@media (max-width: 640px)': {
            gridTemplateColumns: '1fr',
        },
    },
    infoCardLabel: {
        fontSize: 11,
        fontWeight: 700,
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        marginBottom: theme.spacing(0.5),
    },
    infoCardValue: {
        fontSize: 14,
        fontWeight: 700,
        color: theme.palette.text.primary,
        wordBreak: 'break-word',
    },
    infoCardInput: {
        '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
            backgroundColor: theme.palette.background.default,
            paddingLeft: 3,
            paddingRight: 3,
            borderRadius: 2,
        },
        '& .MuiOutlinedInput-root': {
            borderRadius: 4,
            backgroundColor: theme.palette.background.default,
            boxShadow: 'inset 0 0 0 var(--confidence-border-width, 0px) var(--confidence-color, transparent)',
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--confidence-color, rgba(0, 0, 0, 0.23))',
                borderWidth: 'var(--confidence-border-width, 1px)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--confidence-color, rgba(0, 0, 0, 0.87))',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--confidence-color, currentColor)',
                borderWidth: 'var(--confidence-border-width, 2px)',
            },
            '&.Mui-focused': {
                boxShadow: 'inset 0 0 0 var(--confidence-border-width, 0px) var(--confidence-color, transparent)',
            },
        },
        '& .MuiOutlinedInput-input': {
            padding: theme.spacing(0.75, 1),
            fontSize: 13,
        },
    },
    confidenceNotice: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1.25, 1.5),
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.soft}`,
        backgroundColor: theme.palette.surface.soft,
        flexWrap: 'wrap',
    },
    confidenceNoticeTitle: {
        fontSize: 13,
        fontWeight: 700,
        color: theme.palette.text.primary,
    },
    confidenceNoticeText: {
        fontSize: 12,
        color: theme.palette.text.secondary,
    },
    confidenceLegend: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    confidenceChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        padding: theme.spacing(0.5, 1),
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 700,
    },
    confidenceDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0,
    },
    summaryRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        padding: theme.spacing(1.2, 1.5),
        borderTop: `1px solid ${theme.palette.border.subtle}`,
        '&:first-child': {
            borderTop: 'none',
        },
    },
    summaryLabel: {
        fontSize: 13,
        color: theme.palette.text.primary,
    },
    summaryValue: {
        fontSize: 13,
        fontWeight: 700,
        color: theme.palette.text.primary,
        whiteSpace: 'nowrap',
    },
    summaryEmphasis: {
        backgroundColor: '#fbf4df',
    },
    footer: {
        position: 'sticky',
        bottom: 0,
        zIndex: 10,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
        paddingTop: theme.spacing(1.5),
        paddingBottom: theme.spacing(1),
        flexWrap: 'wrap',
        backgroundColor: theme.palette.background.default,
        borderTop: `1px solid ${theme.palette.border.soft}`,
    },
    viewValue: {
        fontSize: 13,
        fontWeight: 500,
        color: theme.palette.text.primary,
        minHeight: 20,
        paddingTop: 2,
        wordBreak: 'break-word',
    },
    viewTextField: {
        pointerEvents: 'none',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--confidence-color, transparent)',
            borderWidth: 'var(--confidence-border-width, 1px)',
        },
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'transparent',
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--confidence-color, transparent)',
            },
        },
        '& .MuiFormHelperText-root': {
            visibility: 'hidden',
        },
        '& .MuiOutlinedInput-input': {
            fontWeight: 500,
        },
    },
    viewTableCell: {
        padding: theme.spacing(0.75, 1),
        backgroundColor: theme.palette.background.default,
        fontSize: 13,
        color: theme.palette.text.primary,
        display: 'flex',
        alignItems: 'center',
        minHeight: 44,
    },
    editButton: {
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        padding: theme.spacing(0.75, 1.75),
        textTransform: 'none',
        fontWeight: 700,
        color: theme.palette.common.white,
        background: `linear-gradient(180deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
        boxShadow: theme.palette.shadow.accent,
        '&:hover': {
            background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        },
    },
}));

const serviceTypeOptions = [
    { value: 'electricidad', label: 'Energía eléctrica' },
    { value: 'agua', label: 'Agua' },
    { value: 'gas', label: 'Gas' },
];

const billClassOptions = ['Factura A', 'Factura B', 'Factura C', 'Nota de Débito', 'Otro'];

const CONFIDENCE_META = {
    ALTO: {
        label: 'Precisión alta',
        color: '#2e7d32',
        softColor: '#e8f5e9',
    },
    MEDIO: {
        label: 'Precisión media',
        color: '#ed6c02',
        softColor: '#fff3e0',
    },
    BAJO: {
        label: 'Precisión baja',
        color: '#d32f2f',
        softColor: '#ffebee',
    },
};

const FORM_FIELD_CONFIDENCE_MAP = {
    proveedor: 'proveedor_nombre',
    idCliente: 'numero_cuenta',
    razonSocial: 'cliente_razon_social',
    cuit: 'cliente_cuit',
    lecturaAnterior: 'lectura_anterior',
    lecturaActual: 'lectura_actual',
    numeroFactura: 'numero_factura',
    domicilio: 'cliente_domicilio',
    periodoDesde: 'periodo_desde',
    periodoHasta: 'periodo_hasta',
    fechaEmision: 'fecha_emision',
    fechaVencimiento: 'fecha_vencimiento',
    consumo: 'consumo_kwh',
};

const CHARGE_ITEM_CONFIDENCE_MAP = {
    'Cargo fijo': {
        cantidad: 'cargo_fijo',
        unidad: 'cargo_fijo',
        subtotal: 'cargo_fijo',
    },
    'Cargo variable': {
        cantidad: 'consumo_kwh',
        unidad: 'unidad_lectura',
        subtotal: 'cargo_variable',
    },
    'Potencia contratada': {
        cantidad: 'potencia_contratada_kw',
        unidad: 'potencia_contratada_kw',
        subtotal: 'potencia_contratada_costo',
    },
    'Potencia demandada': {
        cantidad: 'potencia_demandada_kw',
        unidad: 'potencia_demandada_kw',
        subtotal: 'potencia_demandada_costo',
    },
    'Potencia excedida': {
        cantidad: 'potencia_excedida_kw',
        unidad: 'potencia_excedida_kw',
        subtotal: 'potencia_excedida_costo',
    },
};

const emptyItem = (concepto = '') => ({
    concepto,
    cantidad: '',
    unidad: '',
    tarifa: '',
    subtotal: '',
});

const emptySideItem = label => ({
    label,
    subtotal: '',
});

const toNumber = value => {
    if (value === null || value === undefined || value === '') {
        return 0;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }
    const str = String(value).trim();
    let normalized;
    if (str.includes(',')) {
        // Argentine format: dots are thousands separators, comma is decimal
        normalized = str.replace(/\./g, '').replace(',', '.').replace(/[^\d.-]/g, '');
    } else {
        // Standard format: dot is decimal separator
        normalized = str.replace(/[^\d.-]/g, '');
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
};

const formatDecimal = value => {
    if (value === '' || value === null || value === undefined) return '';
    const num = toNumber(value);
    if (num === 0 && String(value).trim() !== '0') return value;
    return new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
};

const formatCurrency = value =>
    new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(toNumber(value));

const getFieldsMap = bill =>
    (Array.isArray(bill?.campos_todos) ? bill.campos_todos : []).reduce((acc, field) => {
        acc[field.field] = field;
        return acc;
    }, {});

const getFieldValue = (fieldsMap, key, fallback = '') => fieldsMap?.[key]?.value || fallback;

const getConfidenceMeta = confidence => CONFIDENCE_META[confidence] || null;

const getItemConfidence = (fieldsMap, fieldName) => fieldsMap?.[fieldName]?.confidence || null;

const getWorstConfidence = (...levels) => {
    if (levels.includes('BAJO')) {
        return 'BAJO';
    }
    if (levels.includes('MEDIO')) {
        return 'MEDIO';
    }
    if (levels.includes('ALTO')) {
        return 'ALTO';
    }
    return null;
};

const buildChargeItemsFromFields = fieldsMap => {
    const consumo = getFieldValue(fieldsMap, 'consumo_kwh');
    const unidad = getFieldValue(fieldsMap, 'unidad_lectura', 'kWh');
    const cargoFijo = getFieldValue(fieldsMap, 'cargo_fijo');
    const cargoVariable = getFieldValue(fieldsMap, 'cargo_variable');
    const potenciaContratadaKw = getFieldValue(fieldsMap, 'potencia_contratada_kw');
    const potenciaContratadaCosto = getFieldValue(fieldsMap, 'potencia_contratada_costo');
    const potenciaDemandadaKw = getFieldValue(fieldsMap, 'potencia_demandada_kw');
    const potenciaDemandadaCosto = getFieldValue(fieldsMap, 'potencia_demandada_costo');
    const potenciaExcedidaKw = getFieldValue(fieldsMap, 'potencia_excedida_kw');
    const potenciaExcedidaCosto = getFieldValue(fieldsMap, 'potencia_excedida_costo');
    const cargoFijoConfidence = getItemConfidence(fieldsMap, 'cargo_fijo');
    const consumoConfidence = getItemConfidence(fieldsMap, 'consumo_kwh');
    const unidadLecturaConfidence = getItemConfidence(fieldsMap, 'unidad_lectura');
    const cargoVariableConfidence = getItemConfidence(fieldsMap, 'cargo_variable');
    const potenciaContratadaKwConfidence = getItemConfidence(fieldsMap, 'potencia_contratada_kw');
    const potenciaContratadaCostoConfidence = getItemConfidence(fieldsMap, 'potencia_contratada_costo');
    const potenciaDemandadaKwConfidence = getItemConfidence(fieldsMap, 'potencia_demandada_kw');
    const potenciaDemandadaCostoConfidence = getItemConfidence(fieldsMap, 'potencia_demandada_costo');
    const potenciaExcedidaKwConfidence = getItemConfidence(fieldsMap, 'potencia_excedida_kw');
    const potenciaExcedidaCostoConfidence = getItemConfidence(fieldsMap, 'potencia_excedida_costo');

    return [
        {
            concepto: 'Cargo fijo',
            cantidad: cargoFijo ? 1 : '',
            unidad: cargoFijo ? 'mes' : '',
            tarifa: '',
            subtotal: cargoFijo,
            confidence: {
                concepto: cargoFijoConfidence,
                cantidad: getItemConfidence(fieldsMap, 'cargo_fijo'),
                unidad: getItemConfidence(fieldsMap, 'cargo_fijo'),
                subtotal: getItemConfidence(fieldsMap, 'cargo_fijo'),
            },
        },
        {
            concepto: 'Cargo variable',
            cantidad: consumo,
            unidad,
            tarifa: '',
            subtotal: cargoVariable,
            confidence: {
                concepto: getWorstConfidence(consumoConfidence, unidadLecturaConfidence, cargoVariableConfidence),
                cantidad: consumoConfidence,
                unidad: unidadLecturaConfidence,
                subtotal: cargoVariableConfidence,
            },
        },
        {
            concepto: 'Potencia contratada',
            cantidad: potenciaContratadaKw,
            unidad: potenciaContratadaKw ? 'kW' : '',
            tarifa: '',
            subtotal: potenciaContratadaCosto,
            confidence: {
                concepto: getWorstConfidence(potenciaContratadaKwConfidence, potenciaContratadaCostoConfidence),
                cantidad: potenciaContratadaKwConfidence,
                unidad: potenciaContratadaKwConfidence,
                subtotal: potenciaContratadaCostoConfidence,
            },
        },
        {
            concepto: 'Potencia demandada',
            cantidad: potenciaDemandadaKw,
            unidad: potenciaDemandadaKw ? 'kW' : '',
            tarifa: '',
            subtotal: potenciaDemandadaCosto,
            confidence: {
                concepto: getWorstConfidence(potenciaDemandadaKwConfidence, potenciaDemandadaCostoConfidence),
                cantidad: potenciaDemandadaKwConfidence,
                unidad: potenciaDemandadaKwConfidence,
                subtotal: potenciaDemandadaCostoConfidence,
            },
        },
        {
            concepto: 'Potencia excedida',
            cantidad: potenciaExcedidaKw,
            unidad: potenciaExcedidaKw ? 'kW' : '',
            tarifa: '',
            subtotal: potenciaExcedidaCosto,
            confidence: {
                concepto: getWorstConfidence(potenciaExcedidaKwConfidence, potenciaExcedidaCostoConfidence),
                cantidad: potenciaExcedidaKwConfidence,
                unidad: potenciaExcedidaKwConfidence,
                subtotal: potenciaExcedidaCostoConfidence,
            },
        },
    ].filter(item => Object.values(item).some(Boolean));
};

const buildChargeItems = bill => {
    const fieldsMap = getFieldsMap(bill);

    if (Array.isArray(bill?.detalle_facturacion) && bill.detalle_facturacion.length > 0) {
        return bill.detalle_facturacion.map(item => ({
            concepto: item.concepto || item.label || '',
            cantidad: item.cantidad ?? item.valor ?? '',
            unidad: item.unidad || '',
            tarifa: item.tarifa ?? item.rate ?? '',
            subtotal: item.subtotal ?? item.importe ?? item.total ?? '',
            confidence: item.confidence
                ? item.confidence
                : CHARGE_ITEM_CONFIDENCE_MAP[item.concepto || item.label || '']
                  ? Object.entries(CHARGE_ITEM_CONFIDENCE_MAP[item.concepto || item.label || '']).reduce((acc, [part, fieldName]) => {
                        acc[part] = getItemConfidence(fieldsMap, fieldName);
                        return acc;
                    }, {})
                  : undefined,
        }));
    }

    const fieldsChargeItems = buildChargeItemsFromFields(fieldsMap);
    if (fieldsChargeItems.length > 0) {
        return fieldsChargeItems;
    }

    return [
        {
            concepto: 'Cargo fijo',
            cantidad: bill?.factura_papel?.fixed_charge ? 1 : '',
            unidad: 'mes',
            tarifa: bill?.factura?.fixed_charge || '',
            subtotal: bill?.factura_papel?.fixed_charge || '',
        },
        {
            concepto: 'Energía',
            cantidad: bill?.factura_papel?.energy_use?.total || bill?.energy_use_total || bill?.consumo || '',
            unidad: 'kWh',
            tarifa: '',
            subtotal: bill?.factura_papel?.energy_cost?.total || '',
        },
        {
            concepto: 'Potencia Contratada',
            cantidad: bill?.factura_papel?.hired_demand?.total || '',
            unidad: 'kW',
            tarifa: '',
            subtotal: bill?.factura_papel?.hired_demand_cost?.total || '',
        },
        {
            concepto: 'Potencia Adquirida',
            cantidad: bill?.factura_papel?.peak_demand?.total || '',
            unidad: 'kW',
            tarifa: '',
            subtotal: bill?.factura_papel?.peak_demand_cost?.total || '',
        },
    ].filter(item => Object.values(item).some(Boolean));
};

const buildSideItems = (bill, key, fallback) => {
    if (Array.isArray(bill?.[key]) && bill[key].length > 0) {
        return bill[key].map(item => ({
            label: item.label || item.concepto || item.name || '',
            subtotal: item.subtotal ?? item.total ?? item.valor ?? '',
        }));
    }
    return fallback.filter(item => item.subtotal !== '' && item.subtotal !== null && item.subtotal !== undefined);
};

const normalizeInvoice = bill => {
    const fieldsMap = getFieldsMap(bill);
    const chargeItems = buildChargeItems(bill || {});
    const chargesTotal = chargeItems.reduce((acc, item) => acc + toNumber(item.subtotal), 0);
    const totalField = getFieldValue(fieldsMap, 'total') || bill?.total || '';
    const inferredTaxes = Math.max(toNumber(totalField) - chargesTotal, 0);

    return {
        proveedor: bill?.proveedor_nombre || bill?.proveedor_nombre || getFieldValue(fieldsMap, 'proveedor_nombre') || '',
        proveedorId: bill?.proveedor_id ? String(bill.proveedor_id) : '',
        sitio: bill?.jerarquia_nombre || bill?.location_name || bill?.location?.name || (bill?.es_huerfana ? 'Factura sin vincular' : ''),
        tipoServicio: bill?.tipo_servicio || bill?.tipo || 'electricidad',
        idCliente: bill?.cliente_id || bill?.id_cliente || getFieldValue(fieldsMap, 'numero_cuenta') || bill?.nro_cuenta || '',
        idSitio: bill?.jerarquia_id || bill?.id_sitio || '',
        numeroMedidor: bill?.numero_medidor || bill?.medidor || getFieldValue(fieldsMap, 'lectura_actual') || '',
        numeroFactura: bill?.numero_factura || getFieldValue(fieldsMap, 'numero_factura') || '',
        clase: bill?.clase || bill?.categoria_factura || 'Factura B',
        archivoNombre: bill?.archivo_nombre || bill?.filename || bill?.file_name || '',
        archivoUrl: bill?.archivo_url || bill?.file_url || '',
        score: bill?.score ?? '',
        score_original: bill?.score_original ?? null,
        periodoDesde: bill?.periodo_desde || getFieldValue(fieldsMap, 'periodo_desde') || '',
        periodoHasta: bill?.periodo_hasta || getFieldValue(fieldsMap, 'periodo_hasta') || '',
        fechaEmision: bill?.fecha_emision || getFieldValue(fieldsMap, 'fecha_emision') || '',
        fechaVencimiento: bill?.fecha_vencimiento || getFieldValue(fieldsMap, 'fecha_vencimiento') || '',
        consumo: getFieldValue(fieldsMap, 'consumo_kwh') || '',
        unidadLectura: getFieldValue(fieldsMap, 'unidad_lectura', 'kWh') || 'kWh',
        razonSocial: getFieldValue(fieldsMap, 'cliente_razon_social') || bill?.cliente_razon_social || '',
        cuit: getFieldValue(fieldsMap, 'cliente_cuit') || '',
        domicilio: getFieldValue(fieldsMap, 'cliente_domicilio') || '',
        lecturaAnterior: getFieldValue(fieldsMap, 'lectura_anterior') || '',
        lecturaActual: getFieldValue(fieldsMap, 'lectura_actual') || '',
        cargos: chargeItems,
        impuestos: buildSideItems(bill || {}, 'impuestos', [
            { label: 'Impuestos y cargos', subtotal: inferredTaxes || '' },
        ]),
        ajustes: buildSideItems(bill || {}, 'ajustes', [
            { label: 'Ajustes', subtotal: '' },
        ]),
        markdown: bill?.markdown || '',
    };
};

const sumItems = items => items.reduce((acc, item) => acc + toNumber(item.subtotal), 0);

const CORRECTABLE_FIELDS = [
    { formField: 'idCliente', apiField: 'numero_cuenta' },
    { formField: 'razonSocial', apiField: 'cliente_razon_social' },
    { formField: 'cuit', apiField: 'cliente_cuit' },
    { formField: 'lecturaAnterior', apiField: 'lectura_anterior' },
    { formField: 'lecturaActual', apiField: 'lectura_actual' },
    { formField: 'numeroFactura', apiField: 'numero_factura' },
    { formField: 'domicilio', apiField: 'cliente_domicilio' },
    { formField: 'periodoDesde', apiField: 'periodo_desde' },
    { formField: 'periodoHasta', apiField: 'periodo_hasta' },
    { formField: 'fechaEmision', apiField: 'fecha_emision' },
    { formField: 'fechaVencimiento', apiField: 'fecha_vencimiento' },
];

const CORRECTABLE_FORM_FIELDS = new Set(CORRECTABLE_FIELDS.map(f => f.formField));

export default function BillInvoiceEditor({
    bill,
    editable = true,
    loading = false,
    onChange,
    onSave,
    onCancel,
    onDelete,
    onRefresh,
    onSelectFile,
    onViewFile,
}) {
    const classes = useStyles();
    const normalizedBill = useMemo(() => normalizeInvoice(bill || {}), [bill]);
    const [form, setForm] = useState(normalizedBill);
    const [isEditing] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [localCorregidos, setLocalCorregidos] = useState(() => new Set());
    const [savingField, setSavingField] = useState(null);
    const [localizacionNodes, setLocalizacionNodes] = useState([]);
    const [localizacionesLoading, setLocalizacionesLoading] = useState(false);
    const [proveedores, setProveedores] = useState([]);
    const [proveedoresLoading, setProveedoresLoading] = useState(false);
    const originalValuesRef = useRef(null);
    const isCreate = !bill?.id && !bill?.pk;
    const fieldsMap = useMemo(() => getFieldsMap(bill || {}), [bill]);

    useEffect(() => {
        if (!isCreate) return;
        setProveedoresLoading(true);
        getProveedoresLector({ tipo_servicio: form.tipoServicio })
            .then(r => setProveedores(Array.isArray(r?.data?.results) ? r.data.results.map(p => ({ id: String(p.id), name: p.nombre })) : []))
            .catch(() => setProveedores([]))
            .finally(() => setProveedoresLoading(false));
    }, [isCreate, form.tipoServicio]);

    useEffect(() => {
        setLocalizacionesLoading(true);
        getNodes()
            .then(response => {
                const nodes = Array.isArray(response?.data)
                    ? response.data
                    : response?.data?.results || [];
                setLocalizacionNodes(nodes.filter(n => n.level_nombre === 'localización'));
            })
            .catch(() => setLocalizacionNodes([]))
            .finally(() => setLocalizacionesLoading(false));
    }, []);

    useEffect(() => {
        originalValuesRef.current = normalizedBill;
        setForm(normalizedBill);
        setLocalCorregidos(new Set());
    }, [normalizedBill]);

    const confidenceByFormField = useMemo(
        () =>
            Object.entries(FORM_FIELD_CONFIDENCE_MAP).reduce((acc, [formField, sourceField]) => {
                const confidence = fieldsMap?.[sourceField]?.confidence;
                if (confidence) {
                    acc[formField] = confidence;
                }
                return acc;
            }, {}),
        [fieldsMap]
    );

    const corregidoByFormField = useMemo(
        () =>
            Object.entries(FORM_FIELD_CONFIDENCE_MAP).reduce((acc, [formField, sourceField]) => {
                if (fieldsMap?.[sourceField]?.corregido) {
                    acc[formField] = true;
                }
                return acc;
            }, {}),
        [fieldsMap]
    );

    const reviewFields = useMemo(
        () =>
            Object.entries(FORM_FIELD_CONFIDENCE_MAP)
                .map(([formField, sourceField]) => ({
                    formField,
                    label: fieldsMap?.[sourceField]?.label,
                    confidence: confidenceByFormField[formField],
                }))
                .filter(item => item.confidence === 'MEDIO' || item.confidence === 'BAJO'),
        [confidenceByFormField, fieldsMap]
    );

    const reviewChargeFields = useMemo(
        () =>
            Object.entries(CHARGE_ITEM_CONFIDENCE_MAP)
                .map(([label, config]) => ({
                    label,
                    confidence: getWorstConfidence(...Object.values(config).map(fieldName => getItemConfidence(fieldsMap, fieldName))),
                }))
                .filter(item => item.confidence === 'MEDIO' || item.confidence === 'BAJO'),
        [fieldsMap]
    );

    const totals = useMemo(() => {
        const subtotalCargos = sumItems(form.cargos);
        const subtotalImpuestos = sumItems(form.impuestos);
        const subtotalAjustes = sumItems(form.ajustes);
        return {
            subtotalCargos,
            subtotalImpuestos,
            subtotalAjustes,
            total: subtotalCargos + subtotalImpuestos + subtotalAjustes,
        };
    }, [form]);

    const emitChange = nextForm => {
        setForm(nextForm);
        if (onChange) {
            onChange(nextForm);
        }
    };

    const [saveError, setSaveError] = useState('');
    const [unlinking, setUnlinking] = useState(false);
    const [unlinkSuccess, setUnlinkSuccess] = useState(false);

    const handleUnlink = async () => {
        const tipo = bill?.tipo_servicio || bill?.tipo || form.tipoServicio || 'electricidad';
        const pk = bill?.id || bill?.pk;
        if (!pk) return;
        const prevForm = form;
        emitChange({ ...form, sitio: '', idSitio: '' });
        setUnlinking(true);
        try {
            await unlinkBill(tipo, pk);
            setUnlinkSuccess(true);
            onRefresh?.();
        } catch {
            emitChange(prevForm);
        } finally {
            setUnlinking(false);
        }
    };

    const handleSave = async () => {
        const snapshot = form;
        setSaving(true);
        setSaveError('');
        try {
            const tipo = bill?.tipo_servicio || bill?.tipo || snapshot.tipoServicio || 'electricidad';
            const pk = bill?.id || bill?.pk;
            if (pk && originalValuesRef.current) {
                const changed = CORRECTABLE_FIELDS.filter(
                    ({ formField }) => snapshot[formField] !== originalValuesRef.current[formField]
                );
                if (changed.length > 0) {
                    const campos = changed.reduce((acc, { apiField, formField }) => {
                        acc[apiField] = snapshot[formField] ?? null;
                        return acc;
                    }, {});
                    try {
                        const response = await correctBillFields(tipo, pk, campos);
                        const { score } = response.data;
                        snapshot.score = score;
                    } catch (e) {
                        // continue with save even if correction fails
                    }
                }
            }
            const originalIdSitio = String(normalizedBill.idSitio || '');
            const nextIdSitio = String(snapshot.idSitio || '');
            if (pk && nextIdSitio !== originalIdSitio) {
                try {
                    if (nextIdSitio) {
                        await linkBill(tipo, pk, snapshot.idSitio);
                    } else {
                        await unlinkBill(tipo, pk);
                    }
                } catch (e) {
                    // continue with save even if linking/unlinking fails
                }
            }
            await onSave?.(snapshot);
            setSaveSuccess(true);
        } catch (e) {
            const data = e?.response?.data;
            const msg = data
                ? Object.values(data).flat().join(' ')
                : 'Error al guardar la factura.';
            setSaveError(msg || 'Error al guardar la factura.');
        } finally {
            setSaving(false);
        }
    };

    const handleCancelEdit = () => {
        originalValuesRef.current = null;
        setForm(normalizedBill);
        onCancel?.(normalizedBill);
    };

    const handleConfirmField = async formField => {
        const correctable = CORRECTABLE_FIELDS.find(f => f.formField === formField);
        if (!correctable) return;
        const tipo = bill?.tipo_servicio || bill?.tipo || form.tipoServicio || 'electricidad';
        const pk = bill?.id || bill?.pk;
        if (!pk) return;
        setSavingField(formField);
        try {
            await correctBillFields(tipo, pk, { [correctable.apiField]: form[formField] ?? null });
            setLocalCorregidos(prev => new Set([...prev, formField]));
            onRefresh?.();
        } catch (e) {
            // ignore
        } finally {
            setSavingField(null);
        }
    };

    const updateField = field => event => {
        emitChange({
            ...form,
            [field]: event.target.value,
        });
    };

    const updateListItem = (listName, index, field) => event => {
        const nextList = form[listName].map((item, itemIndex) =>
            itemIndex === index ? { ...item, [field]: event.target.value } : item
        );
        emitChange({
            ...form,
            [listName]: nextList,
        });
    };

    const addChargeItem = () => {
        emitChange({
            ...form,
            cargos: [...form.cargos, emptyItem('Nuevo concepto')],
        });
    };

    const addSideItem = listName => {
        emitChange({
            ...form,
            [listName]: [...form[listName], emptySideItem('Nuevo concepto')],
        });
    };

    const renderInput = ({ label, value, onChange: handleChange, select = false, options = [], type = 'text', confidence, apiField, numeric = false, corregido = false, formField = '' }) => {
        if (!isEditing) {
            const displayValue = select
                ? (options.find(o => o === value) || value || '-')
                : numeric
                    ? (formatDecimal(value) || '-')
                    : (value || '-');
            const confidenceMeta = getConfidenceMeta(confidence);
            const needsReview = confidence === 'MEDIO' || confidence === 'BAJO';
            const isLocallyCorregido = localCorregidos.has(formField);
            const canConfirm = needsReview && CORRECTABLE_FORM_FIELDS.has(formField) && !corregido && !isLocallyCorregido;
            return (
                <Box className={classes.fieldGroup}>
                    <Typography className={classes.fieldLabel}>{label}</Typography>
                    <TextField
                        fullWidth
                        variant='outlined'
                        size='small'
                        value={displayValue}
                        className={`${classes.textField} ${classes.viewTextField}`}
                        helperText={needsReview ? confidenceMeta?.label : ' '}
                        FormHelperTextProps={
                            needsReview && confidenceMeta
                                ? { style: { color: confidenceMeta.color, fontWeight: 600, marginLeft: 0, visibility: 'visible' } }
                                : undefined
                        }
                        InputProps={{ readOnly: true }}
                        inputProps={{ tabIndex: -1 }}
                        style={
                            confidenceMeta
                                ? { '--confidence-color': confidenceMeta.color, '--confidence-border-width': '2px' }
                                : undefined
                        }
                    />
                    {(corregido || isLocallyCorregido) && (
                        <Box style={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: -16 }}>
                            <EditOutlinedIcon style={{ fontSize: 11, color: '#2586bc' }} />
                            <Typography style={{ fontSize: 10, color: '#2586bc', fontWeight: 700 }}>Editado</Typography>
                        </Box>
                    )}
                    {canConfirm && (
                        <Tooltip title='Confirmar que el valor extraído es correcto'>
                            <Box
                                component='span'
                                onClick={savingField !== formField ? () => handleConfirmField(formField) : undefined}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 2, cursor: savingField === formField ? 'wait' : 'pointer', color: '#2e7d32' }}
                            >
                                {savingField === formField
                                    ? <CircularProgress size={11} style={{ color: 'inherit' }} />
                                    : <CheckCircleOutlineIcon style={{ fontSize: 13 }} />
                                }
                                <Typography style={{ fontSize: 10, fontWeight: 700, color: 'inherit' }}>Confirmar</Typography>
                            </Box>
                        </Tooltip>
                    )}
                </Box>
            );
        }

        const confidenceMeta = getConfidenceMeta(confidence);
        const needsReview = confidence === 'MEDIO' || confidence === 'BAJO';
        const isLocallyCorregido = localCorregidos.has(formField);
        const canConfirm = needsReview && CORRECTABLE_FORM_FIELDS.has(formField) && !corregido && !isLocallyCorregido;

        return (
            <Box className={classes.fieldGroup}>
                <Typography className={classes.fieldLabel}>{label}</Typography>
                <TextField
                    fullWidth
                    variant='outlined'
                    size='small'
                    select={select}
                    type={type}
                    value={value}
                    onChange={handleChange}
                    disabled={loading}
                    className={classes.textField}
                    InputLabelProps={type === 'date' ? { shrink: true } : undefined}
                    helperText={needsReview ? confidenceMeta?.label : ' '}
                    FormHelperTextProps={
                        needsReview && confidenceMeta
                            ? {
                                  style: {
                                      color: confidenceMeta.color,
                                      fontWeight: 600,
                                      marginLeft: 0,
                                  },
                              }
                            : undefined
                    }
                    style={
                        confidenceMeta
                            ? {
                                  '--confidence-color': confidenceMeta.color,
                                  '--confidence-border-width': '2px',
                              }
                            : undefined
                    }
                >
                    {select
                        ? options.map(option => (
                              <MenuItem key={option} value={option}>
                                  {option}
                              </MenuItem>
                          ))
                        : null}
                </TextField>
                {canConfirm && (
                    <Tooltip title='Confirmar que el valor extraído es correcto'>
                        <Box
                            component='span'
                            onClick={savingField !== formField ? () => handleConfirmField(formField) : undefined}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: -14, marginBottom: 4, cursor: savingField === formField ? 'wait' : 'pointer', color: '#2e7d32' }}
                        >
                            {savingField === formField
                                ? <CircularProgress size={11} style={{ color: 'inherit' }} />
                                : <CheckCircleOutlineIcon style={{ fontSize: 13 }} />
                            }
                            <Typography style={{ fontSize: 10, fontWeight: 700, color: 'inherit' }}>Confirmar</Typography>
                        </Box>
                    </Tooltip>
                )}
            </Box>
        );
    };

    const renderHeaderDateField = ({ label, value, onChange: handleChange, confidence, corregido = false, formField = '' }) => {
        if (!isEditing) {
            const formatted = value ? value.split('-').reverse().join('/') : '-';
            const confidenceMeta = getConfidenceMeta(confidence);
            const needsReview = confidence === 'MEDIO' || confidence === 'BAJO';
            const isLocallyCorregido = localCorregidos.has(formField);
            const canConfirm = needsReview && CORRECTABLE_FORM_FIELDS.has(formField) && !corregido && !isLocallyCorregido;
            return (
                <Box>
                    <TextField
                        fullWidth
                        variant='outlined'
                        size='small'
                        value={formatted}
                        label={label}
                        InputLabelProps={{ shrink: true }}
                        className={`${classes.infoCardInput} ${classes.viewTextField}`}
                        helperText={needsReview ? confidenceMeta?.label : ' '}
                        FormHelperTextProps={
                            needsReview && confidenceMeta
                                ? { style: { color: confidenceMeta.color, fontWeight: 600, marginLeft: 0, visibility: 'visible' } }
                                : undefined
                        }
                        InputProps={{ readOnly: true }}
                        inputProps={{ tabIndex: -1 }}
                        style={
                            confidenceMeta
                                ? { '--confidence-color': confidenceMeta.color, '--confidence-border-width': '2px' }
                                : undefined
                        }
                    />
                    {(corregido || isLocallyCorregido) && (
                        <Box style={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: -16 }}>
                            <EditOutlinedIcon style={{ fontSize: 11, color: '#2586bc' }} />
                            <Typography style={{ fontSize: 10, color: '#2586bc', fontWeight: 700 }}>Editado</Typography>
                        </Box>
                    )}
                    {canConfirm && (
                        <Tooltip title='Confirmar que el valor extraído es correcto'>
                            <Box
                                component='span'
                                onClick={savingField !== formField ? () => handleConfirmField(formField) : undefined}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 2, cursor: savingField === formField ? 'wait' : 'pointer', color: '#2e7d32' }}
                            >
                                {savingField === formField
                                    ? <CircularProgress size={11} style={{ color: 'inherit' }} />
                                    : <CheckCircleOutlineIcon style={{ fontSize: 13 }} />
                                }
                                <Typography style={{ fontSize: 10, fontWeight: 700, color: 'inherit' }}>Confirmar</Typography>
                            </Box>
                        </Tooltip>
                    )}
                </Box>
            );
        }

        const confidenceMeta = getConfidenceMeta(confidence);
        const needsReview = confidence === 'MEDIO' || confidence === 'BAJO';

        return (
            <TextField
                fullWidth
                variant='outlined'
                size='small'
                type='date'
                value={value || ''}
                onChange={handleChange}
                disabled={loading}
                className={classes.infoCardInput}
                InputLabelProps={{ shrink: true }}
                label={label}
                helperText={needsReview ? confidenceMeta?.label : ' '}
                FormHelperTextProps={
                    needsReview && confidenceMeta
                        ? {
                              style: {
                                  color: confidenceMeta.color,
                                  fontWeight: 600,
                                  marginLeft: 0,
                              },
                          }
                        : undefined
                }
                style={
                    confidenceMeta
                        ? {
                              '--confidence-color': confidenceMeta.color,
                              '--confidence-border-width': '2px',
                          }
                        : undefined
                }
            />
        );
    };

    const getConfidenceFieldStyle = confidence => {
        const confidenceMeta = getConfidenceMeta(confidence);

        return confidenceMeta
            ? {
                  '--confidence-color': confidenceMeta.color,
                  '--confidence-border-width': '2px',
              }
            : undefined;
    };

    const getChargeItemConceptConfidence = item =>
        item?.confidence?.concepto ||
        getWorstConfidence(item?.confidence?.cantidad, item?.confidence?.unidad, item?.confidence?.tarifa, item?.confidence?.subtotal);

    return (
        <Box className={classes.root}>
            <Box style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Typography variant='h6' className={classes.pageTitle} style={{ marginBottom: 0 }}>
                        Editar factura
                    </Typography>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Button
                            size='small'
                            className={classes.secondaryButton}
                            startIcon={<DownloadIcon />}
                            onClick={() => onViewFile?.(form)}
                            disabled={!form.archivoUrl}
                        >
                            Descargar archivo
                        </Button>
                    </Box>
                </Box>
                <Tooltip
                    title={
                        <Box style={{ padding: '4px 0' }}>
                            <Typography style={{ fontWeight: 700, fontSize: 13, marginBottom: 4, color: '#fff' }}>Indicadores de precisión</Typography>
                            <Typography style={{ fontSize: 12, color: '#fff' }}>
                                Los bordes muestran la precisión del dato extraído. Revisar campos en amarillo y rojo.
                            </Typography>
                            {(reviewFields.length || reviewChargeFields.length) ? (
                                <Typography style={{ fontSize: 12, marginTop: 4, color: '#fff' }}>
                                    {`Pendientes: ${[...reviewFields, ...reviewChargeFields]
                                        .map(item => `${item.label || item.formField} (${item.confidence.toLowerCase()})`)
                                        .join(', ')}`}
                                </Typography>
                            ) : null}
                            <Box style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                                {Object.entries(CONFIDENCE_META).map(([level, meta]) => (
                                    <Box key={level} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: '#fff' }}>
                                        <Box style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: meta.color, flexShrink: 0 }} />
                                        {meta.label}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    }
                    arrow
                    placement='left'
                >
                    <IconButton size='small'>
                        <HelpOutlineIcon fontSize='small' style={{ color: '#2586bc' }} />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box className={classes.infoGrid}>
                <Box className={classes.infoCard}>
                    <Typography className={classes.infoCardLabel}>Período</Typography>
                    <Box className={classes.infoCardFields}>
                        {renderHeaderDateField({
                            label: 'Desde',
                            value: form.periodoDesde,
                            onChange: updateField('periodoDesde'),
                            confidence: confidenceByFormField.periodoDesde,
                            corregido: corregidoByFormField.periodoDesde,
                            formField: 'periodoDesde',
                        })}
                        {renderHeaderDateField({
                            label: 'Hasta',
                            value: form.periodoHasta,
                            onChange: updateField('periodoHasta'),
                            confidence: confidenceByFormField.periodoHasta,
                            corregido: corregidoByFormField.periodoHasta,
                            formField: 'periodoHasta',
                        })}
                    </Box>
                </Box>
                <Box className={classes.infoCard}>
                    <Typography className={classes.infoCardLabel}>Emisión / Vencimiento</Typography>
                    <Box className={classes.infoCardFields}>
                        {renderHeaderDateField({
                            label: 'Emisión',
                            value: form.fechaEmision,
                            onChange: updateField('fechaEmision'),
                            confidence: confidenceByFormField.fechaEmision,
                            corregido: corregidoByFormField.fechaEmision,
                            formField: 'fechaEmision',
                        })}
                        {renderHeaderDateField({
                            label: 'Vencimiento',
                            value: form.fechaVencimiento,
                            onChange: updateField('fechaVencimiento'),
                            confidence: confidenceByFormField.fechaVencimiento,
                            corregido: corregidoByFormField.fechaVencimiento,
                            formField: 'fechaVencimiento',
                        })}
                    </Box>
                </Box>
                <Box
                    className={classes.infoCard}
                    style={
                        getConfidenceMeta(confidenceByFormField.consumo)
                            ? { borderColor: getConfidenceMeta(confidenceByFormField.consumo).color }
                            : undefined
                    }
                >
                    <Typography className={classes.infoCardLabel}>Consumo</Typography>
                    <Typography className={classes.infoCardValue}>
                        {form.consumo ? `${form.consumo} ${form.unidadLectura || ''}`.trim() : '-'}
                    </Typography>
                    {confidenceByFormField.consumo === 'MEDIO' || confidenceByFormField.consumo === 'BAJO' ? (
                        <Typography
                            className={classes.confidenceNoticeText}
                            style={{ color: getConfidenceMeta(confidenceByFormField.consumo)?.color, marginTop: 4, fontWeight: 600 }}
                        >
                            {getConfidenceMeta(confidenceByFormField.consumo)?.label}
                        </Typography>
                    ) : null}
                </Box>
                <Box className={classes.infoCard}>
                    <Typography className={classes.infoCardLabel}>Precisión general</Typography>
                    <Typography className={classes.infoCardValue}>
                        {form.score !== '' && form.score !== null && form.score !== undefined ? `${form.score}%` : '-'}
                    </Typography>
                    {form.score_original !== null && form.score_original !== undefined && (
                        <Tooltip title='Confianza original del OCR, antes de correcciones'>
                            <Typography style={{ cursor: 'default', marginTop: 4, fontSize: 12 }}>
                                OCR: {form.score_original}%
                            </Typography>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={12} md={3} className={classes.column}>
                    <Paper elevation={0} className={classes.panel}>
                        <Box className={classes.sectionHeader}>
                            <Box className={classes.sectionHeaderLeft}>
                                <ReceiptOutlinedIcon fontSize='small' />
                                Factura energética
                            </Box>
                        </Box>

                        <Box className={classes.panelBody}>
                            <Box className={classes.fieldGroup}>
                                <Typography className={classes.fieldLabel}>Proveedor</Typography>
                                {isCreate ? (
                                    <Autocomplete
                                        options={proveedores}
                                        getOptionLabel={o => o.name}
                                        getOptionSelected={(o, v) => o.id === v.id}
                                        value={proveedores.find(p => p.id === String(form.proveedorId)) || null}
                                        onChange={(_, v) => emitChange({ ...form, proveedor: v?.name || '', proveedorId: v?.id || '' })}
                                        disabled={loading || proveedoresLoading}
                                        noOptionsText='Sin resultados'
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                variant='outlined'
                                                size='small'
                                                placeholder='Buscar proveedor...'
                                                className={classes.textField}
                                                helperText=' '
                                            />
                                        )}
                                    />
                                ) : (
                                    <TextField
                                        fullWidth
                                        variant='outlined'
                                        size='small'
                                        value={form.proveedor || '-'}
                                        className={`${classes.textField} ${classes.viewTextField}`}
                                        helperText=' '
                                        InputProps={{ readOnly: true }}
                                        inputProps={{ tabIndex: -1 }}
                                    />
                                )}
                            </Box>
                            <Box className={classes.fieldGroup}>
                                <Typography className={classes.fieldLabel}>Localización</Typography>
                                {isEditing ? (
                                    <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                                        <TextField
                                            fullWidth
                                            select
                                            variant='outlined'
                                            size='small'
                                            value={String(form.idSitio || '')}
                                            onChange={e => {
                                                if (!e.target.value) {
                                                    handleUnlink();
                                                } else {
                                                    const node = localizacionNodes.find(n => String(n.id) === e.target.value);
                                                    emitChange({ ...form, sitio: node?.nombre || '', idSitio: e.target.value });
                                                }
                                            }}
                                            disabled={loading || localizacionesLoading}
                                            className={classes.textField}
                                            helperText=' '
                                        >
                                            <MenuItem value=''>
                                                <em>Sin localización</em>
                                            </MenuItem>
                                            {localizacionNodes.map(node => (
                                                <MenuItem key={node.id} value={String(node.id)}>
                                                    {node.nombre}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <Tooltip title='Desvincular localización'>
                                            <span>
                                                <IconButton
                                                    size='small'
                                                    disabled={!form.idSitio || loading || unlinking}
                                                    onClick={handleUnlink}
                                                    style={{ marginTop: 4 }}
                                                >
                                                    <LinkOffIcon fontSize='small' />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                ) : (
                                    <TextField
                                        fullWidth
                                        variant='outlined'
                                        size='small'
                                        value={form.sitio || '-'}
                                        className={`${classes.textField} ${classes.viewTextField}`}
                                        helperText=' '
                                        InputProps={{ readOnly: true }}
                                        inputProps={{ tabIndex: -1 }}
                                    />
                                )}
                            </Box>

                            {renderInput({ label: 'ID Localización', value: form.idSitio })}

                            <Box className={classes.fieldGroup}>
                                <Typography className={classes.fieldLabel}>Tipo de servicio</Typography>
                                {isEditing ? (
                                    <RadioGroup
                                        row
                                        value={form.tipoServicio}
                                        onChange={updateField('tipoServicio')}
                                        className={classes.serviceTypeGroup}
                                    >
                                        {serviceTypeOptions.map(option => (
                                            <FormControlLabel
                                                key={option.value}
                                                value={option.value}
                                                control={<Radio color='primary' size='small' />}
                                                label={option.label}
                                                disabled={loading}
                                            />
                                        ))}
                                    </RadioGroup>
                                ) : (
                                    <RadioGroup
                                        row
                                        value={form.tipoServicio}
                                        className={classes.serviceTypeGroup}
                                        style={{ pointerEvents: 'none' }}
                                    >
                                        {serviceTypeOptions.map(option => (
                                            <FormControlLabel
                                                key={option.value}
                                                value={option.value}
                                                control={<Radio color='primary' size='small' />}
                                                label={option.label}
                                                disabled
                                            />
                                        ))}
                                    </RadioGroup>
                                )}
                            </Box>

                            <Box className={classes.compactFieldsGrid}>
                                {renderInput({
                                    label: 'ID Cliente',
                                    value: form.idCliente,
                                    onChange: updateField('idCliente'),
                                    confidence: confidenceByFormField.idCliente,
                                    corregido: corregidoByFormField.idCliente,
                                    formField: 'idCliente',
                                })}
                                {renderInput({
                                    label: 'CUIT',
                                    value: form.cuit,
                                    onChange: updateField('cuit'),
                                    confidence: confidenceByFormField.cuit,
                                    corregido: corregidoByFormField.cuit,
                                    formField: 'cuit',
                                })}
                            </Box>
                            {renderInput({
                                label: 'Razón social',
                                value: form.razonSocial,
                                onChange: updateField('razonSocial'),
                                confidence: confidenceByFormField.razonSocial,
                                corregido: corregidoByFormField.razonSocial,
                                formField: 'razonSocial',
                            })}
                            {renderInput({
                                label: 'Número de factura',
                                value: form.numeroFactura,
                                onChange: updateField('numeroFactura'),
                                confidence: confidenceByFormField.numeroFactura,
                                corregido: corregidoByFormField.numeroFactura,
                                formField: 'numeroFactura',
                            })}
                            {renderInput({
                                label: 'Clase',
                                value: form.clase,
                                onChange: updateField('clase'),
                                select: true,
                                options: billClassOptions,
                            })}
                            {renderInput({
                                label: 'Domicilio',
                                value: form.domicilio,
                                onChange: updateField('domicilio'),
                                confidence: confidenceByFormField.domicilio,
                                corregido: corregidoByFormField.domicilio,
                                formField: 'domicilio',
                            })}

                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={9} className={classes.column}>
                    <Box width='100%'>
                        <Paper elevation={0} className={classes.panel}>
                            <Box className={classes.sectionHeaderNeutral}>
                                <Box className={classes.sectionHeaderLeft}>
                                    <ReceiptOutlinedIcon fontSize='small' />
                                    Detalle de facturación
                                </Box>
                            </Box>

                            <Box className={classes.panelBody}>
                                <Box className={classes.itemsTable}>
                                    <Box className={classes.tableRow}>
                                        <Box className={classes.tableHeaderCell}>Concepto</Box>
                                        <Box className={classes.tableHeaderCell}>Cantidad</Box>
                                        <Box className={classes.tableHeaderCell}>Unidad</Box>
                                        <Box className={classes.tableHeaderCell}>Tarifa ($)</Box>
                                        <Box className={classes.tableHeaderCell}>Subtotal ($)</Box>
                                    </Box>

                                    {form.cargos.map((item, index) => (
                                        <Box key={`${item.concepto}-${index}`} className={classes.tableRow}>
                                            <Box className={classes.tableCell}>
                                                <TextField
                                                    fullWidth
                                                    variant='outlined'
                                                    size='small'
                                                    value={item.concepto}
                                                    onChange={updateListItem('cargos', index, 'concepto')}
                                                    disabled={loading}
                                                    className={`${classes.tableTextField} ${classes.conceptField} ${!isEditing ? classes.viewTextField : ''}`}
                                                    style={getConfidenceFieldStyle(getChargeItemConceptConfidence(item))}
                                                    InputProps={!isEditing ? { readOnly: true } : undefined}
                                                    inputProps={!isEditing ? { tabIndex: -1 } : undefined}
                                                />
                                            </Box>
                                            <Box className={classes.tableCell}>
                                                <TextField
                                                    fullWidth
                                                    variant='outlined'
                                                    size='small'
                                                    value={!isEditing ? formatDecimal(item.cantidad) : item.cantidad}
                                                    onChange={updateListItem('cargos', index, 'cantidad')}
                                                    disabled={loading}
                                                    className={`${classes.tableTextField} ${!isEditing ? classes.viewTextField : ''}`}
                                                    style={getConfidenceFieldStyle(item?.confidence?.cantidad)}
                                                    InputProps={!isEditing ? { readOnly: true } : undefined}
                                                    inputProps={!isEditing ? { tabIndex: -1 } : undefined}
                                                />
                                            </Box>
                                            <Box className={classes.tableCell}>
                                                <TextField
                                                    fullWidth
                                                    variant='outlined'
                                                    size='small'
                                                    value={item.unidad}
                                                    onChange={updateListItem('cargos', index, 'unidad')}
                                                    disabled={loading}
                                                    className={`${classes.tableTextField} ${classes.conceptField} ${!isEditing ? classes.viewTextField : ''}`}
                                                    style={getConfidenceFieldStyle(item?.confidence?.unidad)}
                                                    InputProps={!isEditing ? { readOnly: true } : undefined}
                                                    inputProps={!isEditing ? { tabIndex: -1 } : undefined}
                                                />
                                            </Box>
                                            <Box className={classes.tableCell}>
                                                <TextField
                                                    fullWidth
                                                    variant='outlined'
                                                    size='small'
                                                    value={!isEditing ? formatDecimal(item.tarifa) : item.tarifa}
                                                    onChange={updateListItem('cargos', index, 'tarifa')}
                                                    disabled={loading}
                                                    className={`${classes.tableTextField} ${!isEditing ? classes.viewTextField : ''}`}
                                                    style={getConfidenceFieldStyle(item?.confidence?.tarifa)}
                                                    InputProps={!isEditing ? { readOnly: true } : undefined}
                                                    inputProps={!isEditing ? { tabIndex: -1 } : undefined}
                                                />
                                            </Box>
                                            <Box className={classes.tableCell}>
                                                <TextField
                                                    fullWidth
                                                    variant='outlined'
                                                    size='small'
                                                    value={!isEditing ? formatDecimal(item.subtotal) : item.subtotal}
                                                    onChange={updateListItem('cargos', index, 'subtotal')}
                                                    disabled={loading}
                                                    className={`${classes.tableTextField} ${!isEditing ? classes.viewTextField : ''}`}
                                                    style={getConfidenceFieldStyle(item?.confidence?.subtotal)}
                                                    InputProps={!isEditing ? { readOnly: true } : undefined}
                                                    inputProps={!isEditing ? { tabIndex: -1 } : undefined}
                                                />
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>

                                {isEditing ? (
                                    <Button color='primary' className={classes.addLink} onClick={addChargeItem}>
                                        + Agregar concepto
                                    </Button>
                                ) : null}

                                <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
                                    {renderInput({
                                        label: 'Lectura anterior',
                                        value: form.lecturaAnterior,
                                        onChange: updateField('lecturaAnterior'),
                                        confidence: confidenceByFormField.lecturaAnterior,
                                        corregido: corregidoByFormField.lecturaAnterior,
                                        formField: 'lecturaAnterior',
                                        numeric: true,
                                    })}
                                    {renderInput({
                                        label: 'Lectura actual',
                                        value: form.lecturaActual || form.numeroMedidor,
                                        onChange: event => emitChange({ ...form, lecturaActual: event.target.value }),
                                        confidence: confidenceByFormField.lecturaActual,
                                        corregido: corregidoByFormField.lecturaActual,
                                        formField: 'lecturaActual',
                                        numeric: true,
                                    })}
                                </Box>
                            </Box>
                        </Paper>

                        

                        <Paper elevation={0} className={classes.sideSection} style={{ marginTop: 16 }}>
                            <Box className={classes.sectionHeaderNeutral}>
                                <Box className={classes.sectionHeaderLeft}>Resumen</Box>
                            </Box>
                            <Box className={classes.summaryRow}>
                                <Typography className={classes.summaryLabel}>Subtotal cargos</Typography>
                                <Typography className={classes.summaryValue}>{formatCurrency(totals.subtotalCargos)}</Typography>
                            </Box>
                            <Box className={classes.summaryRow}>
                                <Typography className={classes.summaryLabel}>Impuestos</Typography>
                                <Typography className={classes.summaryValue}>{formatCurrency(totals.subtotalImpuestos)}</Typography>
                            </Box>
                            <Box className={classes.summaryRow}>
                                <Typography className={classes.summaryLabel}>Ajustes</Typography>
                                <Typography className={classes.summaryValue}>{formatCurrency(totals.subtotalAjustes)}</Typography>
                            </Box>
                            <Divider />
                            <Box className={`${classes.summaryRow} ${classes.summaryEmphasis}`}>
                                <Typography className={classes.summaryLabel}>Total factura</Typography>
                                <Typography className={classes.summaryValue}>{formatCurrency(totals.total)}</Typography>
                            </Box>
                        </Paper>

                        <Box className={classes.footer}>
                            {isEditing ? (
                                <>
                                    <Button
                                        className={classes.primaryButton}
                                        startIcon={<SaveOutlinedIcon />}
                                        onClick={handleSave}
                                        disabled={loading || saving}
                                    >
                                        {saving ? 'Guardando...' : 'Guardar factura'}
                                    </Button>
                                    <Button
                                        color='secondary'
                                        variant='contained'
                                        className={classes.dangerButton}
                                        onClick={() => onDelete?.(form)}
                                        disabled={loading}
                                    >
                                        Eliminar
                                    </Button>
                                    <Button className={classes.secondaryButton} onClick={handleCancelEdit} disabled={loading}>
                                        Cancelar
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        color='secondary'
                                        variant='contained'
                                        className={classes.dangerButton}
                                        onClick={() => onDelete?.(form)}
                                        disabled={loading}
                                    >
                                        Eliminar
                                    </Button>
                                    <Button className={classes.secondaryButton} onClick={() => onCancel?.(form)} disabled={loading}>
                                        Cerrar
                                    </Button>
                                </>
                            )}
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            <Snackbar
                open={saveSuccess}
                autoHideDuration={3000}
                onClose={() => setSaveSuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity='success' onClose={() => setSaveSuccess(false)}>
                    Factura guardada correctamente.
                </Alert>
            </Snackbar>

            <Snackbar
                open={unlinkSuccess}
                autoHideDuration={3000}
                onClose={() => setUnlinkSuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity='success' onClose={() => setUnlinkSuccess(false)}>
                    Localización desvinculada correctamente.
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!saveError}
                autoHideDuration={6000}
                onClose={() => setSaveError('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity='error' onClose={() => setSaveError('')}>
                    {saveError}
                </Alert>
            </Snackbar>

        </Box>
    );
}
